/* eslint-disable @typescript-eslint/ban-ts-comment */
import SelectForm from "@feniks-pwa/web-app/web/Components/Common/Form/Select/selectForm";
import DOMPurify from "dompurify";
import { Form, Formik, FormikProps, FormikValues } from "formik";
import { FC, Fragment, useMemo, useRef } from "react";

import {
  BIRTH_DATE_MAX,
  BIRTH_DATE_MIN,
} from "web/Pages/Checkout/Subscription/BenefitSummary/ActiveForm/constants";
import { typesWithValidations } from "web/Pages/Checkout/Subscription/BenefitSummary/ActiveForm/utils";
import { FORM_RECEIVERS } from "web/Pages/Checkout/Subscription/BenefitSummary/BenefitForm/constants";

import { DatepickerFormik } from "web/Layout/Datepicker";
import __ from "web/Layout/Translations";

import Checkbox from "web/Components/Common/Form/Checkbox";
import Field from "web/Components/Common/Form/Field";
import RequirePeselForm from "web/Components/Common/Form/RequirePeselForm/requirePeselForm";
import ToolTip from "web/Components/Common/ToolTip";

import isArrayHasItems from "web/utils/data/validator/array/isArrayHasItems";
import { peselMask } from "web/utils/peselMask";
import { validateFields } from "web/utils/system/formValidator/validation";

import { PropsWithClasses, SimpleFn } from "web/types/Common";
import {
  ContentTypeActiveFormData,
  HeaderTypeActiveFormData,
  IActiveFormValues,
  SubscriptionActiveFormData,
  SubscriptionActiveFormDataType,
  SubscriptionActiveFormField,
  SubscriptionActiveFormFieldType,
  SubscriptionFormReceiver,
} from "web/types/Subscription";

import classify from "web/classify";

import ActiveFormSubmitButton from "../ActiveFormSubmitButton";
import defaultClasses from "./activeForm.scss";

const checkboxClass = {
  check: defaultClasses.check,
  icon: defaultClasses.icon,
  customLabel: defaultClasses.activeFormCustomLabel,
  checkError: defaultClasses.checkError,
  labelText: defaultClasses.activeFormLabelText,
};

interface ICheckoutSubscriptionActiveFormProps {
  data: SubscriptionActiveFormData[];
  onSubmit: (
    submittedValues: IActiveFormValues,
    formId?: string,
    isAutomaticallySet?: boolean,
    isEditMode?: boolean,
    formUUID?: string,
    receiver?: SubscriptionFormReceiver
  ) => void;
  formId?: string;
  formValues?: IActiveFormValues;
  receiver?: SubscriptionFormReceiver;
  isEditMode?: boolean;
  formUUID?: string;
  close: SimpleFn;
  allowChangingPersonalData?: boolean;
  isExternal?: boolean;
}

interface IFieldToValidate {
  name: string;
  rules: (string | { name: string; additional: string })[];
  ignoreSubResultValidation: boolean;
}

const CheckoutSubscriptionActiveForm: FC<
  PropsWithClasses<ICheckoutSubscriptionActiveFormProps>
> = ({
  classes = {},
  data,
  onSubmit,
  formId,
  formValues,
  receiver,
  isEditMode = false,
  formUUID,
  close,
  allowChangingPersonalData = false,
  isExternal = false,
}) => {
  const ref = useRef<FormikProps<FormikValues>>(null);

  const getHeader = (header: HeaderTypeActiveFormData) => {
    return (
      <strong className={classes.header} key={header.id}>
        {header?.value}
      </strong>
    );
  };

  const getContent = (content: ContentTypeActiveFormData) => {
    if (!content.content) {
      return null;
    }

    const contentElement = (
      <div
        className={classes.content}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(content?.content),
        }}
      />
    );
    return (
      <div className={`cke_editable ${classes.activeFormEditableCKE}`} key={content.id}>
        <strong className={classes.title}>{content.name}</strong>
        <div className={classes.contentWrapper}>
          {content?.useCheckbox ? (
            <span className={classes.checkbox}>
              <Checkbox name={content.id} classes={checkboxClass}>
                <span className={classes.useCheckboxText}>
                  {contentElement}
                </span>
              </Checkbox>
            </span>
          ) : (
            contentElement
          )}
        </div>
      </div>
    );
  };

  const [initialValues, fieldsToValidate] = useMemo(() => {
    const activeForm = data;
    const init: IActiveFormValues = {};
    const toValidate: IFieldToValidate[] = [];

    if (isArrayHasItems(activeForm)) {
      activeForm.forEach((section) => {
        if (section.type === "form") {
          section.fields.forEach((field) => {
            const {
              id,
              type,
              require: isFieldRequired,
              requirementDependency,
            } = field;
            const fieldValidationRules = [];

            switch (type) {
              case SubscriptionActiveFormFieldType.CHECKBOX:
                init[id] = !!formValues?.[id];
                break;
              case SubscriptionActiveFormFieldType.REQUIRE_PESEL:
                init[id] = formValues?.[id]
                  ? formValues[id]
                  : {
                      pesel: "",
                      birthDate: null,
                      babyOrForeigner: false,
                    };
                break;
              default:
                init[id] = formValues?.[id] ? formValues[id] : "";
            }

            if (type && typesWithValidations.includes(type)) {
              fieldValidationRules.push(type);
            }

            if (type === SubscriptionActiveFormFieldType.NUMBER) {
              fieldValidationRules.push("numeric");
            }

            if (isFieldRequired) {
              fieldValidationRules.push("required");
            }

            if (requirementDependency) {
              const dependentOn = section.fields.find(
                (item) => item.id === requirementDependency
              );
              // check if given field is dependent on requirePesel field
              const isDependentOnRequirePesel =
                dependentOn?.type ===
                SubscriptionActiveFormFieldType.REQUIRE_PESEL;

              if (isDependentOnRequirePesel) {
                fieldValidationRules.push({
                  name: "dependentOnRequirePesel",
                  additional: requirementDependency,
                });
              } else if (dependentOn?.require) {
                fieldValidationRules.push("required");
              }
            }

            toValidate.push({
              name: field.id,
              rules: fieldValidationRules,
              ignoreSubResultValidation:
                type === SubscriptionActiveFormFieldType.REQUIRE_PESEL,
            });
          });
        } else if (section.type === "content" && section.useCheckbox) {
          init[section.id] = !!formValues?.[section.id];

          if (section.checkboxRequired) {
            toValidate.push({
              name: section.id,
              rules: ["required"],
              ignoreSubResultValidation: false,
            });
          }
        }
      });
    }
    return [init, toValidate];
  }, [data]);

  const getField = (field: SubscriptionActiveFormField) => {
    let input;
    switch (field.type) {
      case SubscriptionActiveFormFieldType.REQUIRE_PESEL:
        input = (
          <RequirePeselForm
            field={field}
            checkboxClass={checkboxClass}
            classes={classes}
            tooltipMessage={field.tooltip}
          />
        );
        break;
      case SubscriptionActiveFormFieldType.SELECT:
        input = (
          <SelectForm
            boldTextIfActive
            name={field.id}
            placeholder={
              (initialValues[field.id] as string) || __("Wybierz opcje")
            }
            options={field.options.map((option) => ({
              value: option.value,
              label: option.label,
            }))}
            hasBackgroundWhenActive
          />
        );
        break;
      case SubscriptionActiveFormFieldType.CHECKBOX:
        input = (
          <Checkbox
            uppercase
            name={field.id}
            classes={checkboxClass}
            tooltipMessage={field.tooltip}
          >
            <span
              className={`${classes.label} ${classes.checkboxLabel}`}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(field.label),
              }}
            />
          </Checkbox>
        );
        break;
      case SubscriptionActiveFormFieldType.BIRTHDATE:
      case SubscriptionActiveFormFieldType.DATE: {
        const isBirthDate =
          field.type === SubscriptionActiveFormFieldType.BIRTHDATE;

        const minDateValue = isBirthDate ? BIRTH_DATE_MIN : null;
        const maxDateValue = isBirthDate ? BIRTH_DATE_MAX : null;

        input = (
          <div
            className={`${classes.inputWrapper} ${classes.dataInputWrapper}`}
          >
            <DatepickerFormik
              name={field.id}
              label={field.label}
              placeholder={field.label}
              className={classes.input}
              minDate={minDateValue}
              maxDate={maxDateValue}
            />
          </div>
        );
        break;
      }
      case SubscriptionActiveFormFieldType.POSTCODE:
        input = <Field label={field.label} name={field.id} />;
        break;
      case SubscriptionActiveFormFieldType.FIRSTNAME:
      case SubscriptionActiveFormFieldType.LASTNAME: {
        const isFieldDisabled =
          !isEditMode &&
          !allowChangingPersonalData &&
          receiver === FORM_RECEIVERS.EMPLOYEE;
        input = (
          <Field
            label={field.label}
            name={field.id}
            disabled={isFieldDisabled}
          />
        );
        break;
      }
      case SubscriptionActiveFormFieldType.PESEL: {
        input = <Field label={field.label} name={field.id} mask={peselMask} />;
        break;
      }
      case SubscriptionActiveFormFieldType.NUMBER: {
        input = (
          <Field label={field.label} name={field.id} enableNumericHandler />
        );
        break;
      }
      default: {
        input = <Field label={field.label} name={field.id} type={field.type} />;
      }
    }

    const doesFieldHaveTooltip = field.tooltip?.trim();

    return (
      <Fragment key={field.id}>
        <div className={classes.tooltipWrapper}>
          {field.type !== SubscriptionActiveFormFieldType.CHECKBOX &&
            field.type !== SubscriptionActiveFormFieldType.REQUIRE_PESEL && (
              <dl className={classes.label}>
                {field.type === SubscriptionActiveFormFieldType.SELECT
                  ? field.title
                  : __(field.label)}
                {doesFieldHaveTooltip && (
                  <ToolTip
                    customStyles={{
                      root: classes.tooltipWrapper,
                      tooltip: classes.tooltip,
                    }}
                    message={field.tooltip}
                    variant="right"
                  />
                )}
              </dl>
            )}
        </div>
        {input}
      </Fragment>
    );
  };

  const formData = useMemo(() => {
    if (!isArrayHasItems(data)) return null;

    return data.map((section) => {
      switch (section.type) {
        case SubscriptionActiveFormDataType.HEADER:
          return getHeader(section);
        case SubscriptionActiveFormDataType.CONTENT:
          return getContent(section);
        case SubscriptionActiveFormDataType.FORM:
          return (
            <Fragment key={section.id}>
              {section.fields.map((key) => getField(key))}
              <div className={classes.separator} />
            </Fragment>
          );
        default:
          return null;
      }
    });
  }, [data]);

  const validate = validateFields(fieldsToValidate);

  const submitHandler = (submittedValues: IActiveFormValues) => {
    if (!ref.current?.dirty && !isExternal) {
      close();
      return;
    }
    onSubmit(submittedValues, formId, false, isEditMode, formUUID, receiver);
  };

  return (
    <Formik
      enableReinitialize
      // @ts-ignore
      initialValues={initialValues}
      onSubmit={submitHandler}
      // @ts-ignore
      validate={validate}
      innerRef={ref}
    >
      <Form noValidate>
        <div className={classes.root}>{formData}</div>
        <ActiveFormSubmitButton />
      </Form>
    </Formik>
  );
};

export default classify<PropsWithClasses<ICheckoutSubscriptionActiveFormProps>>(
  defaultClasses
)(CheckoutSubscriptionActiveForm);
